import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { isAdmin } from '../../utils/permissions';
import useTranslation from '../../hooks/useTranslation';
import Icon from '../../components/AppIcon';
import organizationTradesService from '../../services/organizationTradesService';
import { statusVariant } from '../../utils/statusVariant';
import Badge from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// ─── Inline Edit Row ────────────────────────────────────────────────────────────
import { PAGE_GROUPS } from '../../constants/pageKeys';
import { sortTemplatesByRole } from '../../constants/roles';
import {
  listTemplates,
  createTemplate,
  duplicateTemplate,
  updateTemplate,
  deleteTemplate,
  resetSystemTemplate,
} from '../../services/permissionTemplateService';

// ─── Editeur de gabarits de permissions ────────────────────────
// Meme habillage que la carte « Corps de metier » juste au-dessus : en-tete
// avec titre, sous-titre et compteur, puis le corps. On empile une seconde
// carte, on n'introduit pas d'onglets.
const TemplateEditor = ({ t }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);       // gabarit deplie
  const [draftName, setDraftName] = useState('');
  const [draftPages, setDraftPages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);        // { type, text }
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingReset, setPendingReset] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await listTemplates();
    setTemplates(sortTemplatesByRole(data));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const say = (type, text) => {
    setNotice({ type, text });
    if (type === 'success') setTimeout(() => setNotice(null), 3000);
  };

  const openEditor = (tpl) => {
    setOpenId(tpl?.id);
    setDraftName(tpl?.name || '');
    setDraftPages(Array.isArray(tpl?.pages) ? [...tpl?.pages] : []);
    setNotice(null);
  };

  const togglePage = (key) =>
    setDraftPages((prev) =>
      prev?.includes(key) ? prev?.filter((k) => k !== key) : [...prev, key]
    );

  const handleSave = async () => {
    if (!draftName?.trim()) { say('error', t('orgSettings.templates.nameRequired')); return; }
    setSaving(true);
    const { error, blocked } = await updateTemplate(openId, { name: draftName, pages: draftPages });
    setSaving(false);
    if (blocked) { say('error', t(`orgSettings.templates.blocked.${blocked}`)); return; }
    if (error) { say('error', t('orgSettings.templates.error')); return; }
    say('success', t('orgSettings.templates.saved'));
    setOpenId(null);
    await load();
  };

  const handleCreate = async () => {
    setSaving(true);
    const { data, error } = await createTemplate(t('orgSettings.templates.newTemplate'), []);
    setSaving(false);
    if (error || !data) { say('error', t('orgSettings.templates.error')); return; }
    await load();
    openEditor(data);
    say('success', t('orgSettings.templates.created'));
  };

  const handleDuplicate = async (tpl) => {
    setSaving(true);
    const { data, error } = await duplicateTemplate(
      tpl,
      t('orgSettings.templates.duplicateSuffix', { name: tpl?.name })
    );
    setSaving(false);
    if (error || !data) { say('error', t('orgSettings.templates.error')); return; }
    await load();
    openEditor(data);
  };

  /**
   * Retour a la configuration livree. Ce n'est PAS une edition : on jette les
   * cases en cours et on relit ce que la base vient de restaurer, sinon
   * l'ecran continuerait d'afficher le brouillon abandonne.
   */
  const handleReset = async (tpl) => {
    setPendingReset(null);
    setSaving(true);
    const { error, message } = await resetSystemTemplate(tpl?.id);
    setSaving(false);

    if (message) { say('error', message); return; }          // message de la fonction
    if (error) { say('error', t('orgSettings.templates.error')); return; }

    const { data } = await listTemplates();
    setTemplates(sortTemplatesByRole(data));
    const fresh = (data || [])?.find((x) => x?.id === tpl?.id);
    if (fresh) openEditor(fresh);
    say('success', t('orgSettings.templates.resetDone'));
  };

  const handleDelete = async (tpl) => {
    const { error, blocked } = await deleteTemplate(tpl);
    setPendingDelete(null);
    if (blocked) {
      say('error', t(`orgSettings.templates.blocked.${blocked}`, {
        name: tpl?.name, count: tpl?.userCount,
      }));
      return;
    }
    if (error) { say('error', t('orgSettings.templates.error')); return; }
    say('success', t('orgSettings.templates.deleted'));
    if (openId === tpl?.id) setOpenId(null);
    await load();
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden mt-6">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[#2C3E50]">{t('orgSettings.templates.sectionTitle')}</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">{t('orgSettings.templates.sectionSubtitle')}</p>
        </div>
        <span className="text-xs text-[#6B7280] bg-muted px-2 py-1 rounded-full">
          {templates?.length} {t('orgSettings.templates.templateCount')}
        </span>
      </div>

      {notice && (
        <div className={`mx-4 mt-3 px-3 py-2 rounded-lg flex items-start gap-2 border ${
          notice?.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
        }`}>
          <Icon
            name={notice?.type === 'error' ? 'AlertCircle' : 'CheckCircle2'}
            size={14}
            className={`${notice?.type === 'error' ? 'text-red-500' : 'text-green-600'} flex-shrink-0 mt-0.5`}
          />
          <span className={`text-xs ${notice?.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>
            {notice?.text}
          </span>
          <button onClick={() => setNotice(null)} className="ml-auto flex-shrink-0">
            <Icon name="X" size={12} className="text-gray-400" />
          </button>
        </div>
      )}

      <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between gap-3">
        <p className="text-[11px] text-[#6B7280]">{t('orgSettings.templates.systemEditable')}</p>
        <button
          onClick={handleCreate}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60 flex-shrink-0"
        >
          <Icon name="Plus" size={13} />
          {t('orgSettings.templates.newTemplate')}
        </button>
      </div>

      {loading ? (
        <div className="px-4 py-8 flex justify-center">
          <Icon name="Loader2" size={18} className="animate-spin text-primary" />
        </div>
      ) : templates?.length === 0 ? (
        <p className="px-4 py-8 text-center text-xs text-[#6B7280] italic">
          {t('orgSettings.templates.noneYet')}
        </p>
      ) : (
        <div className="divide-y divide-border">
          {templates?.map((tpl) => {
            const isOpen = openId === tpl?.id;
            return (
              <div key={tpl?.id}>
                <div className="px-4 py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[#2C3E50] truncate">{tpl?.name}</span>
                      {tpl?.is_system && (
                        <span className="text-[10px] font-semibold text-[#6B7280] bg-muted px-1.5 py-0.5 rounded">
                          {t('orgSettings.templates.systemBadge')}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-[#6B7280] mt-0.5">
                      {t('orgSettings.templates.usersCount', { count: tpl?.userCount })}
                      {' · '}
                      {tpl?.pages?.length || 0} / {PAGE_GROUPS?.reduce((n, g) => n + (g?.pages?.length || 0), 0)}
                    </p>
                  </div>
                  <button
                    onClick={() => (isOpen ? setOpenId(null) : openEditor(tpl))}
                    className="px-2.5 py-1 text-[11px] font-medium text-[#2C3E50] bg-white border border-border rounded-md hover:border-primary hover:bg-orange-50 transition-colors"
                  >
                    {isOpen ? t('orgSettings.templates.cancel') : t('orgSettings.templates.edit')}
                  </button>
                  <button
                    onClick={() => handleDuplicate(tpl)}
                    disabled={saving}
                    title={t('orgSettings.templates.duplicate')}
                    className="p-1.5 rounded-md hover:bg-muted transition-colors disabled:opacity-60"
                  >
                    <Icon name="Copy" size={14} className="text-[#6B7280]" />
                  </button>
                  {/* Un gabarit systeme n'offre pas de corbeille : le trigger la
                      refuserait, autant ne pas la proposer. */}
                  {!tpl?.is_system && (
                    <button
                      onClick={() => setPendingDelete(tpl)}
                      title={t('orgSettings.templates.delete')}
                      className="p-1.5 rounded-md hover:bg-red-50 transition-colors"
                    >
                      <Icon name="Trash2" size={14} className="text-red-500" />
                    </button>
                  )}
                </div>

                {isOpen && (
                  <div className="px-4 pb-4 bg-muted/20">
                    <div className="mb-3">
                      <label className="block text-[11px] font-medium text-[#6B7280] mb-1">
                        {t('orgSettings.templates.nameLabel')}
                      </label>
                      <input
                        type="text"
                        value={draftName}
                        onChange={(e) => setDraftName(e?.target?.value)}
                        placeholder={t('orgSettings.templates.namePlaceholder')}
                        className="w-full max-w-sm text-xs border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => setDraftPages(PAGE_GROUPS?.flatMap((g) => g?.pages?.map((pg) => pg?.key)))}
                        className="text-[11px] text-primary hover:underline"
                      >
                        {t('orgSettings.templates.selectAll')}
                      </button>
                      <span className="text-[11px] text-[#D1D5DB]">·</span>
                      <button onClick={() => setDraftPages([])} className="text-[11px] text-primary hover:underline">
                        {t('orgSettings.templates.selectNone')}
                      </button>
                      {/* Reinitialiser n'est pas une edition en cours : c'est
                          l'abandon des modifications et le retour a la
                          configuration livree. D'ou le bouton encadre, a
                          droite, plutot qu'un troisieme lien orange.
                          Gabarits systeme uniquement. */}
                      {tpl?.is_system && (
                        <button
                          onClick={() => setPendingReset(tpl)}
                          disabled={saving}
                          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-[#6B7280] bg-white border border-border rounded-md hover:border-amber-400 hover:text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-60"
                        >
                          <Icon name="RotateCcw" size={11} />
                          {t('orgSettings.templates.reset')}
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {PAGE_GROUPS?.map((group) => (
                        <div key={group?.id}>
                          <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">
                            {t(group?.titleKey)}
                          </p>
                          <div className="space-y-1">
                            {group?.pages?.map((pg) => (
                              <label key={pg?.key} className="flex items-center gap-2 cursor-pointer py-0.5">
                                <input
                                  type="checkbox"
                                  checked={draftPages?.includes(pg?.key)}
                                  onChange={() => togglePage(pg?.key)}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer accent-primary"
                                />
                                <span className="text-xs text-[#2C3E50]">{t(pg?.labelKey)}</span>
                                {pg?.upcoming && (
                                  <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1 rounded">
                                    {t('orgSettings.templates.upcoming')}
                                  </span>
                                )}
                                {pg?.hidden && (
                                  <span className="text-[10px] text-[#9CA3AF]">
                                    ({t('orgSettings.templates.hiddenPage')})
                                  </span>
                                )}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center gap-2 mt-4">
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
                      >
                        {saving ? <Icon name="Loader2" size={12} className="animate-spin" /> : <Icon name="Check" size={12} />}
                        {t('orgSettings.templates.save')}
                      </button>
                      <button
                        onClick={() => setOpenId(null)}
                        className="px-3 py-1.5 text-xs font-medium text-[#6B7280] hover:text-[#2C3E50] hover:bg-muted rounded-lg transition-colors"
                      >
                        {t('orgSettings.templates.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pendingReset && (
        <ConfirmDialog
          isOpen={!!pendingReset}
          title={t('orgSettings.templates.resetConfirmTitle')}
          message={t('orgSettings.templates.resetConfirmMessage', { name: pendingReset?.name })}
          confirmLabel={t('orgSettings.templates.reset')}
          cancelLabel={t('orgSettings.templates.cancel')}
          onConfirm={() => handleReset(pendingReset)}
          onCancel={() => setPendingReset(null)}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          isOpen={!!pendingDelete}
          title={t('orgSettings.templates.deleteConfirmTitle')}
          message={t('orgSettings.templates.deleteConfirmMessage', { name: pendingDelete?.name })}
          confirmVariant="danger"
          confirmLabel={t('orgSettings.templates.delete')}
          cancelLabel={t('orgSettings.templates.cancel')}
          onConfirm={() => handleDelete(pendingDelete)}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};

const TradeRow = ({ trade, onRename, onToggleActive, onDelete, onMoveUp, onMoveDown, isFirst, isLast, t }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(trade?.name || '');
  const [saving, setSaving] = useState(false);

  const handleRenameSubmit = async () => {
    const trimmed = editValue?.trim();
    if (!trimmed || trimmed === trade?.name) {
      setEditing(false);
      setEditValue(trade?.name);
      return;
    }
    setSaving(true);
    try {
      await onRename(trade?.id, trimmed);
      setEditing(false);
    } catch {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') handleRenameSubmit();
    if (e?.key === 'Escape') {
      setEditing(false);
      setEditValue(trade?.name);
    }
  };

  return (
    <div className={`flex items-center gap-3 px-4 py-3 border-b border-border last:border-b-0 ${!trade?.isActive ? 'opacity-50' : ''}`}>
      {/* Reorder buttons */}
      <div className="flex flex-col gap-0.5">
        <button
          onClick={() => onMoveUp(trade?.id)}
          disabled={isFirst}
          className="p-0.5 rounded hover:bg-muted disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
          title={t('orgSettings.trades.moveUp')}
        >
          <Icon name="ChevronUp" size={14} className="text-[#6B7280]" />
        </button>
        <button
          onClick={() => onMoveDown(trade?.id)}
          disabled={isLast}
          className="p-0.5 rounded hover:bg-muted disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
          title={t('orgSettings.trades.moveDown')}
        >
          <Icon name="ChevronDown" size={14} className="text-[#6B7280]" />
        </button>
      </div>
      {/* Name / inline edit */}
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e?.target?.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleKeyDown}
            disabled={saving}
            className="w-full text-sm border border-primary rounded-md px-2 py-1 outline-none focus:ring-2 focus:ring-primary/20 bg-white text-[#2C3E50]"
          />
        ) : (
          <span className="text-sm text-[#2C3E50] font-medium truncate block">{trade?.name}</span>
        )}
      </div>
      {/* Status badge */}
      <Badge variant={statusVariant('user', !!trade?.isActive)}>
        {trade?.isActive ? t('orgSettings.trades.active') : t('orgSettings.trades.inactive')}
      </Badge>
      {/* Actions */}
      <div className="flex items-center gap-1">
        {!editing && (
          <button
            onClick={() => { setEditing(true); setEditValue(trade?.name); }}
            className="p-1.5 rounded-md hover:bg-muted transition-colors"
            title={t('common.edit')}
          >
            <Icon name="Pencil" size={14} className="text-[#6B7280]" />
          </button>
        )}
        <button
          onClick={() => onToggleActive(trade?.id, !trade?.isActive)}
          className="p-1.5 rounded-md hover:bg-muted transition-colors"
          title={trade?.isActive ? t('orgSettings.trades.deactivate') : t('orgSettings.trades.activate')}
        >
          <Icon name={trade?.isActive ? 'EyeOff' : 'Eye'} size={14} className="text-[#6B7280]" />
        </button>
        <button
          onClick={() => onDelete(trade?.id, trade?.name)}
          className="p-1.5 rounded-md hover:bg-red-50 transition-colors"
          title={t('common.delete')}
        >
          <Icon name="Trash2" size={14} className="text-red-500" />
        </button>
      </div>
    </div>
  );
};

// ─── Delete Confirm Modal ───────────────────────────────────────────────────────
const DeleteConfirmModal = ({ tradeName, onConfirm, onCancel, t }) => (
  <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
          <Icon name="Trash2" size={16} className="text-red-600" />
        </div>
        <h3 className="text-base font-semibold text-[#2C3E50]">{t('orgSettings.trades.deleteConfirmTitle')}</h3>
      </div>
      <p className="text-sm text-[#6B7280] mb-5">
        {t('orgSettings.trades.deleteConfirmMessage')?.replace('{name}', tradeName)}
      </p>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm rounded-lg border border-border text-[#6B7280] hover:bg-muted transition-colors"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 text-sm rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
        >
          {t('common.delete')}
        </button>
      </div>
    </div>
  </div>
);

// ─── Main Page ──────────────────────────────────────────────────────────────────
const OrgSettingsPage = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useTranslation();

  const [trades, setTrades] = useState([]);
  const [allTrades, setAllTrades] = useState([]); // includes inactive
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTradeName, setNewTradeName] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [writeError, setWriteError] = useState(null);

  // Admin guard
  if (!isAdmin(userProfile)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center">
          <Icon name="Lock" size={24} className="text-primary" />
        </div>
        <h2 className="text-lg font-semibold text-[#2C3E50]">{t('orgSettings.accessDenied.title')}</h2>
        <p className="text-sm text-[#6B7280] text-center max-w-xs">{t('orgSettings.accessDenied.message')}</p>
        <button
          onClick={() => navigate('/')}
          className="mt-2 px-4 py-2 text-sm rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors"
        >
          {t('orgSettings.accessDenied.goHome')}
        </button>
      </div>
    );
  }

  const loadTrades = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load all trades (active + inactive) for admin management
      const { supabase } = await import('../../lib/supabase');
      const { getCurrentOrgId } = await import('../../lib/getCurrentOrgId');
      const orgId = await getCurrentOrgId();
      if (!orgId) throw new Error('No organization found.');
      const { data, error: dbErr } = await supabase?.from('organization_trades')?.select('id, organization_id, name, order_index, is_active, created_at')?.eq('organization_id', orgId)?.order('order_index', { ascending: true })?.order('name', { ascending: true });
      if (dbErr) throw dbErr;
      const mapped = (data || [])?.map(row => ({
        id: row?.id,
        organizationId: row?.organization_id,
        name: row?.name,
        orderIndex: row?.order_index,
        isActive: row?.is_active,
        createdAt: row?.created_at,
      }));
      setAllTrades(mapped);
    } catch (err) {
      console.error('[OrgSettingsPage.loadTrades]', err);
      setError(err?.message || t('orgSettings.trades.loadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  const handleAdd = async () => {
    const trimmed = newTradeName?.trim();
    if (!trimmed) return;
    setAdding(true);
    setWriteError(null);
    try {
      const maxOrder = allTrades?.length > 0 ? Math.max(...allTrades?.map(t => t?.orderIndex)) + 1 : 1;
      const created = await organizationTradesService?.create({ name: trimmed, orderIndex: maxOrder });
      setAllTrades(prev => [...prev, created]);
      setNewTradeName('');
    } catch (err) {
      setWriteError(err?.message || t('orgSettings.trades.writeError'));
    } finally {
      setAdding(false);
    }
  };

  const handleRename = async (id, newName) => {
    setWriteError(null);
    try {
      const updated = await organizationTradesService?.update(id, { name: newName });
      setAllTrades(prev => prev?.map(t => t?.id === id ? { ...t, name: updated?.name ?? newName } : t));
    } catch (err) {
      setWriteError(err?.message || t('orgSettings.trades.writeError'));
      throw err;
    }
  };

  const handleToggleActive = async (id, newActive) => {
    setWriteError(null);
    try {
      await organizationTradesService?.update(id, { isActive: newActive });
      setAllTrades(prev => prev?.map(t => t?.id === id ? { ...t, isActive: newActive } : t));
    } catch (err) {
      setWriteError(err?.message || t('orgSettings.trades.writeError'));
    }
  };

  const handleDeleteRequest = (id, name) => {
    setDeleteTarget({ id, name });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setWriteError(null);
    try {
      await organizationTradesService?.delete(deleteTarget?.id);
      setAllTrades(prev => prev?.filter(t => t?.id !== deleteTarget?.id));
    } catch (err) {
      setWriteError(err?.message || t('orgSettings.trades.writeError'));
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleMoveUp = async (id) => {
    const idx = allTrades?.findIndex(t => t?.id === id);
    if (idx <= 0) return;
    const reordered = [...allTrades];
    [reordered[idx - 1], reordered[idx]] = [reordered?.[idx], reordered?.[idx - 1]];
    const updated = reordered?.map((t, i) => ({ ...t, orderIndex: i + 1 }));
    setAllTrades(updated);
    setWriteError(null);
    try {
      await Promise.all([
        organizationTradesService?.update(updated?.[idx - 1]?.id, { orderIndex: updated?.[idx - 1]?.orderIndex }),
        organizationTradesService?.update(updated?.[idx]?.id, { orderIndex: updated?.[idx]?.orderIndex }),
      ]);
    } catch (err) {
      setWriteError(err?.message || t('orgSettings.trades.writeError'));
      await loadTrades(); // revert on error
    }
  };

  const handleMoveDown = async (id) => {
    const idx = allTrades?.findIndex(t => t?.id === id);
    if (idx < 0 || idx >= allTrades?.length - 1) return;
    const reordered = [...allTrades];
    [reordered[idx], reordered[idx + 1]] = [reordered?.[idx + 1], reordered?.[idx]];
    const updated = reordered?.map((t, i) => ({ ...t, orderIndex: i + 1 }));
    setAllTrades(updated);
    setWriteError(null);
    try {
      await Promise.all([
        organizationTradesService?.update(updated?.[idx]?.id, { orderIndex: updated?.[idx]?.orderIndex }),
        organizationTradesService?.update(updated?.[idx + 1]?.id, { orderIndex: updated?.[idx + 1]?.orderIndex }),
      ]);
    } catch (err) {
      setWriteError(err?.message || t('orgSettings.trades.writeError'));
      await loadTrades();
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Settings2" size={18} className="text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">{t('orgSettings.pageTitle')}</h1>
        </div>
        <p className="text-sm text-[#6B7280] ml-12">{t('orgSettings.pageSubtitle')}</p>
      </div>
      {/* Corps de métier section */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-[#2C3E50]">{t('orgSettings.trades.sectionTitle')}</h2>
            <p className="text-xs text-[#6B7280] mt-0.5">{t('orgSettings.trades.sectionSubtitle')}</p>
          </div>
          <span className="text-xs text-[#6B7280] bg-muted px-2 py-1 rounded-full">
            {allTrades?.filter(t => t?.isActive)?.length} {t('orgSettings.trades.activeCount')}
          </span>
        </div>

        {/* Write error banner */}
        {writeError && (
          <div className="mx-4 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <Icon name="AlertCircle" size={14} className="text-red-500 flex-shrink-0" />
            <span className="text-xs text-red-700">{writeError}</span>
            <button onClick={() => setWriteError(null)} className="ml-auto">
              <Icon name="X" size={12} className="text-red-400" />
            </button>
          </div>
        )}

        {/* Add new trade */}
        <div className="px-4 py-3 border-b border-border bg-muted/30">
          <div className="flex gap-2">
            <input
              type="text"
              value={newTradeName}
              onChange={(e) => setNewTradeName(e?.target?.value)}
              onKeyDown={(e) => e?.key === 'Enter' && handleAdd()}
              placeholder={t('orgSettings.trades.addPlaceholder')}
              disabled={adding}
              className="flex-1 text-sm border border-border rounded-lg px-3 py-2 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white text-[#2C3E50] placeholder-[#9CA3AF]"
            />
            <button
              onClick={handleAdd}
              disabled={adding || !newTradeName?.trim()}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {adding ? (
                <Icon name="Loader2" size={14} className="animate-spin" />
              ) : (
                <Icon name="Plus" size={14} />
              )}
              {t('common.add')}
            </button>
          </div>
        </div>

        {/* Trade list */}
        {loading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-[#6B7280]">
            <Icon name="Loader2" size={18} className="animate-spin" />
            <span className="text-sm">{t('common.loading')}</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Icon name="AlertCircle" size={24} className="text-red-400" />
            <p className="text-sm text-red-600">{error}</p>
            <button
              onClick={loadTrades}
              className="text-xs text-primary hover:underline"
            >
              {t('orgSettings.trades.retry')}
            </button>
          </div>
        ) : allTrades?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-[#6B7280]">
            <Icon name="Layers" size={24} className="text-[#D1D5DB]" />
            <p className="text-sm">{t('orgSettings.trades.empty')}</p>
          </div>
        ) : (
          <div>
            {allTrades?.map((trade, idx) => (
              <TradeRow
                key={trade?.id}
                trade={trade}
                onRename={handleRename}
                onToggleActive={handleToggleActive}
                onDelete={handleDeleteRequest}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                isFirst={idx === 0}
                isLast={idx === allTrades?.length - 1}
                t={t}
              />
            ))}
          </div>
        )}
      </div>

      {/* Gabarits de permissions — seconde section, meme habillage */}
      <TemplateEditor t={t} />

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <DeleteConfirmModal
          tradeName={deleteTarget?.name}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          t={t}
        />
      )}
    </div>
  );
};

export default OrgSettingsPage;
